<template>
    <div class="register">
        <div class="register__background">
            <img src="~@/assets/images/dashboard-background.png" alt="kasanowa" />
        </div>
        <Form :validation-schema="schema" @submit="onSubmit" class="register__wrapper">
            <img src="~@/assets/images/logo.png" alt="kasanowa" />
            <h2 class="register__title">{{ $t('register.register') }}</h2>
            <KTextField
                id="input-password"
                v-model="registerForm.password"
                name="password"
                label=""
                :placeholder="$t('register.password')"
                type="password"
            />
            <KTextField
                id="input-password-confirmation"
                v-model="registerForm.password_confirmation"
                name="passwordConfirmation"
                label=""
                :placeholder="$t('register.passwordConfirmation')"
                type="password"
            />
            <KCheckbox id="register-rules" v-model="registerForm.rules" label="" hide-details>
                <template v-slot:default>
                    <div class="register__checkbox-wrapper-text kasanowa-hover">
                        <div @click="toggleRuleCheckbox">
                            {{ $t('register.rules') }}
                            <span @click="openRules">{{ $t('register.rulesLink') }}</span>
                        </div>
                    </div>
                </template>
            </KCheckbox>
            <div class="register__btn-wrapper">
                <KButton
                    id="register-btn"
                    :label="$t('register.registerBtn')"
                    type="submit"
                    full-width
                    :loading="loading"
                />
            </div>
        </Form>
    </div>
</template>

<script setup lang="ts">
import { ref } from '@vue/runtime-core'
import router from '@/router'
import { Form } from 'vee-validate'
import * as yup from 'yup'
import AuthApi from '@/api/v1/AuthApi'
import { toast } from 'vue3-toastify'
import { navigateToWithSuccessToast } from '@/hooks/NavigateHooks'
import type { IRegisterRequest } from '@/models/RegisterModel'
import KTextField from '@/components/atoms/KTextField.vue'
import KButton from '@/components/atoms/KButton.vue'
import KCheckbox from '@/components/atoms/KCheckbox.vue'

const registerForm = ref<IRegisterRequest>({
    password: null,
    password_confirmation: null,
    rules: false,
    token: router.currentRoute.value.query.token,
    email: router.currentRoute.value.query.email
})

const schema: any = yup.object({
    password: yup
        .string()
        .required(`${t('register.validation.passwordRequired')}`)
        .label('password'),
    passwordConfirmation: yup
        .string()
        .required(`${t('register.validation.passwordConfirmationRequired')}`)
        .label('passwordConfirmation')
        .test('passwordConfirmation', `${t('register.validation.passwordRegex')}`, function (val) {
            if (val.match(registerForm.value.password)) {
                return true
            } else {
                return false
            }
        })
})

const loading = ref<boolean>(false)
const authApi = new AuthApi()
const onSubmit = async () => {
    loading.value = true
    if (!registerForm.value.rules) {
        toast.error(`${t('register.toast.errorRules')}`)
        loading.value = false
        return
    }
    const request: IRegisterRequest = {
        password: registerForm.value.password,
        password_confirmation: registerForm.value.password_confirmation,
        token: registerForm.value.token,
        email: registerForm.value.email,
        rules: registerForm.value.rules
    }
    try {
        const result = await authApi.register(request)
        if (!result.isSuccess) {
            toast.error(`${t('register.toast.errorRegister')}`)
            loading.value = false
            return
        }
        navigateToWithSuccessToast('/', t('register.toast.successRegister'))
    } catch (e) {
        toast.error(`${t('register.toast.errorRegister')}`)
        loading.value = false
        return
    }
    loading.value = false
}

const toggleRuleCheckbox = () => {
    registerForm.value.rules = !registerForm.value.rules
}
const openRules = () => {
    registerForm.value.rules = false
    window.open(window.location.origin, '_blank')
    setTimeout(() => {
        registerForm.value.rules = false
    }, 1)
}
</script>

<style scoped lang="scss">
.register {
    position: relative;
    width: 100%;
    height: 100vh;
    overflow: hidden;
    margin: 0;
    padding: 0;

    &__background {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
        height: 100%;

        img {
            object-fit: cover;
            width: 100%;
            height: 100%;
        }
    }

    &__wrapper {
        padding: 20px;
        border: 1px solid $secondary-400;
        border-radius: 5px;
        background-color: $white;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        min-width: 280px;
        width: 90%;
        box-shadow: 0 0 5px $secondary-400;
        max-width: 550px;

        img {
            position: absolute;
            left: 50%;
            top: -170px;
            transform: translateX(-50%);

            @include media-breakpoint-up(md) {
                top: -115px;
                left: 26px;
            }
        }
    }

    &__title {
        text-align: center;
        margin-bottom: 22px;
        font-size: 28px;
    }

    &__checkbox-wrapper-text {
        display: flex;
        align-items: center;
        width: 90%;

        span {
            color: $primary-400;
            font-weight: 700;
        }
    }

    &__btn-wrapper {
        margin-top: 20px;
    }
}
</style>
